import { type ThemeNames } from '../../../../../theme';
import { RadioButton } from '../../../selectionControls/radioButton';
import { Checkbox as CheckboxComponent } from '../../../selectionControls/checkbox';
import { useDropdownCellOptionStyles } from './dropdownCellOption.styles';
import { BodyS } from '../../../../../typography';
import { DropdownCellDivider } from '../dropdownCellDivider';

import type { JSX, ReactNode } from 'react';

export enum DropdownCellOptionType {
  Default = 'default',
  Radiobutton = 'radiobutton',
  Checkbox = 'checkbox',
  Icon = 'icon',
  Divider = 'divider',
}

export type DropdownCellOptionVariant = 'default' | 'destructive';

export type DropdownCellOptionProps = {
  label: string;
  sublabel?: string;
  type?: DropdownCellOptionType;
  variant?: DropdownCellOptionVariant;
  icon?: ReactNode;
  iconAfter?: ReactNode;
  isSelected?: boolean;
  isChild?: boolean;
  onOptionClick?: () => void;
  themeName?: ThemeNames;
  searchQuery?: string;
  disabled?: boolean;
};

const highlightMatch = (
  label: string,
  query: string,
): (string | JSX.Element)[] | string => {
  if (!query) {
    return label;
  }

  const regex = new RegExp(`(${query})`, 'giu');
  const parts = label.split(regex);

  return parts.map((part, index) =>
    regex.test(part) ? <strong key={index}>{part}</strong> : part,
  );
};

/**
 * DropdownCellOption is a component that represents an option in a dropdown cell.
 * The structure of the option is as follows: [icon/radio/checkbox] [label] [sublabel]
 * @param label - Main info of the option that floats to the left
 * @param sublabel - Additional info of the option that floats to the right and has tertiary styling
 * @param type - Type of the option. Can be 'default', 'radiobutton', 'checkbox' or 'icon'
 * @param icon - Icon to be displayed to the left of the label; only displayed when type is 'icon'
 */
export const DropdownCellOption = ({
  label,
  sublabel,
  type = DropdownCellOptionType.Default,
  variant = 'default',
  icon,
  iconAfter,
  isSelected = false,
  isChild = false,
  onOptionClick,
  themeName,
  searchQuery = '',
  disabled = false,
}: DropdownCellOptionProps): JSX.Element => {
  const styles = useDropdownCellOptionStyles({ themeName });

  if (type === DropdownCellOptionType.Divider) {
    return <DropdownCellDivider />;
  }

  return (
    <button
      type='button'
      disabled={disabled}
      css={{
        ...styles.button,
        ...styles.base,
        ...(isSelected && styles.selected),
        ...(isChild && styles.childOption),
        ...(variant === 'destructive' && styles.destructive),
        ...(disabled && styles.disabled),
      }}
      onClick={onOptionClick}
    >
      {type === DropdownCellOptionType.Icon && Boolean(icon) && (
        <span css={styles.iconWrapper}>{icon}</span>
      )}
      {type === DropdownCellOptionType.Radiobutton && (
        <RadioButton
          value={label}
          checked={isSelected}
          radioSx={styles.radio}
          tabIndex={-1}
        />
      )}
      {type === DropdownCellOptionType.Checkbox && (
        <CheckboxComponent
          checked={isSelected}
          css={styles.checkbox}
          tabIndex={-1}
        />
      )}
      <BodyS css={styles.label} tag='span'>
        {highlightMatch(label, searchQuery)}
      </BodyS>

      {Boolean(sublabel) && (
        <BodyS css={styles.sublabel} tag='span'>
          {sublabel}
        </BodyS>
      )}

      {Boolean(iconAfter) && <span css={styles.iconWrapper}>{iconAfter}</span>}
    </button>
  );
};
